import {
  Button,
  Card,
  closeCurrentDialog,
  DataStatus,
  EmptyStatus,
  openDeleteDialog,
  openDialog,
} from 'platform/components';
import {Box, Text, VStack} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetIntegratedServicesQuery} from '@dms/api/core';
import {
  useDeleteEmployeeLinkMutation,
  useGetEmployeeIntegrationQuery,
  useGetEmployeeIntegrationsQuery,
} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {handleApiError, Section} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {PairingWithPlanner} from './components/PairingWithPlanner';

interface ExternalSystemsProps extends RequiredTestIdProps {}

export function ExternalSystems(props: ExternalSystemsProps) {
  const {id} = useParams();

  const {
    data: integratedServices,
    isLoading: isIntegratedServicesLoading,
    isError: isIntegratedServicesError,
  } = useGetIntegratedServicesQuery();

  const isPlannerIntegrated = integratedServices?.some((service) => service.code === 'PLANNER');

  const {
    data: employeeIntegration,
    isLoading: isEmployeeIntegrationLoading,
    isError: isEmployeeIntegrationError,
  } = useGetEmployeeIntegrationQuery(
    {employeeId: id ?? ''},
    {skip: isNil(id) || !isPlannerIntegrated}
  );
  const {
    data: employeeIntegrations,
    isLoading: isEmployeeIntegrationsLoading,
    isError: isEmployeeIntegrationsError,
  } = useGetEmployeeIntegrationsQuery(undefined, {skip: !isPlannerIntegrated});
  const [deleteEmployeeLink] = useDeleteEmployeeLinkMutation();

  const handleUnpair = () => {
    if (isNil(id)) {
      return;
    }

    openDeleteDialog({
      onConfirm: () =>
        deleteEmployeeLink({
          employeeId: id,
          contextTarget: 'PLANNER',
        })
          .unwrap()
          .catch(handleApiError),
    });
  };

  const isError =
    isEmployeeIntegrationError || isEmployeeIntegrationsError || isIntegratedServicesError;
  const isLoading =
    isEmployeeIntegrationLoading || isEmployeeIntegrationsLoading || isIntegratedServicesLoading;

  const employeePlannerIntegration = employeeIntegration?.find(
    (e) => e.contextTarget === 'PLANNER'
  );

  const integration = employeeIntegrations?.find(
    (integration) => integration.id === employeePlannerIntegration?.contextId
  );

  return (
    <Section>
      <Card
        title={i18n.t('page.employeeDetail.labels.externalSystem')}
        actions={
          integration
            ? [
                {
                  type: 'button',
                  variant: 'link',
                  title: i18n.t('general.actions.change'),
                  leftIcon: 'navigation/refresh',
                  onClick: () =>
                    openDialog(
                      <PairingWithPlanner
                        employeeId={id!}
                        onClose={closeCurrentDialog}
                        data-testid={suffixTestId('pairWithPlanner', props)}
                      />,
                      {title: i18n.t('general.actions.change'), withAdditionalFooter: true}
                    ),
                },
                {
                  type: 'button',
                  variant: 'dangerLink',
                  title: i18n.t('page.employeeDetail.labels.unpair'),
                  leftIcon: 'navigation/close',
                  onClick: handleUnpair,
                },
              ]
            : undefined
        }
      >
        <DataStatus isLoading={isLoading} isError={isError} isEmpty={!isPlannerIntegrated}>
          {integration ? (
            <VStack spacing={4}>
              <Text size="small" color="secondary">
                {i18n.t('page.employeeDetail.labels.pairingWithPlannerDescription')}
              </Text>
              <Card variant="inlineGrey" title={integration.label} />
            </VStack>
          ) : (
            <Box paddingVertical={6}>
              <VStack spacing={4} align="center">
                <EmptyStatus
                  headline={i18n.t('page.employeeDetail.labels.notPairedWithPlanner')}
                  subheadline={i18n.t('page.employeeDetail.labels.notPairedWithPlannerDescription')}
                  data-testid={suffixTestId('notPaired', props)}
                />
                <Button
                  leftIcon="content/link"
                  variant="secondary"
                  title={i18n.t('page.employeeDetail.actions.pairWithPlanner')}
                  isDisabled={isNilOrEmpty(id)}
                  onClick={() =>
                    openDialog(
                      <PairingWithPlanner
                        employeeId={id!}
                        onClose={closeCurrentDialog}
                        data-testid={suffixTestId('pairWithPlanner', props)}
                      />,
                      {
                        title: i18n.t('page.employeeDetail.actions.pairWithPlanner'),
                        withAdditionalFooter: true,
                      }
                    )
                  }
                  data-testid={suffixTestId('pairWithPlanner', props)}
                />
              </VStack>
            </Box>
          )}
        </DataStatus>
      </Card>
    </Section>
  );
}
